import React from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { TextLog } from 'modules/emergencies/components/ui/texts/LogText';
import FlexParent from 'modules/emergencies/components/ui/layout/FlexParent';
import Separator from 'modules/emergencies/components/ui/layout/Separator';
import LogItemInfoContainer from 'modules/emergencies/components/ui/containers/logItemInfoContainer';
import LogReasonsContainer from 'modules/emergencies/components/ui/containers/logReasonsContainer';
import LogEntryTitleContainer from 'modules/emergencies/components/ui/containers/logEntryTitleContainer';
import BulletPoint from 'modules/emergencies/components/ui/layout/BulletPoint';
import TicketIdButton from 'modules/emergencies/components/ui/buttons/TicketIdButton';
//components
import { TitleLog } from 'modules/emergencies/components/log/logComponents/TitleLog';
import LOG_VERBS from 'modules/emergencies/components/log/logConstants';
import Badge from 'modules/emergencies/components/log/logComponents/Badge';
import ticketService from 'modules/emergencies/ticketService';
import LogItemNotificationFailedBody from 'modules/emergencies/components/log/LogItemNotificationFailedBody';
import LogItemTicketUpdateBody from './LogItemTicketUpdateBody';
import { FormatPrice } from 'utils/format';
import { IntlProvider } from 'react-intl';
import getLogSubtitle from './logSubtitle';

const reasonText = {
  ticket_feedback: {
    LATE: 'Late response',
    ALREADY_COVERED: 'Service covered by another provider',
    NOT_NEEDED: 'I no longer need the service',
    OUT_OF_SCOPE: 'Out of scope',
    OTHER: 'Other reason',
  },
  quote_feedback: {
    LATE: 'Late response',
    ALREADY_COVERED: 'Need already covered',
    TIMING: 'Timing',
    CONDITIONS: 'Conditions do not suit',
    EXPENSIVE: 'Expensive price',
    OTHER: 'Other reason',
  },
};

const ticketUpdatesLogs = [
  LOG_VERBS.DELIVERED,
  LOG_VERBS.DELAYED,
  LOG_VERBS.ON_THE_WAY,
  LOG_VERBS.PICKUP,
];

const LogReasonsFlexComponent = styled(FlexParent)`
  flex-direction: column;
  gap: 8px;
`;

const LogConditionsLabel = styled(TextLog)`
  font-weight: 700;
`;

const LogConditions = styled(TextLog)`
  margin-left: 8px;
`;

const LogItem = ({ className, log = {}, separatorProps, IDProps, config }) => {
  const { hasID, onIDClick } = IDProps || {};
  const badgeVariants = {
    internal: 'gray',
    shipper: 'blue',
    system: 'dark-gray',
    carrier: 'green',
  };
  const logsWithoutSubtitle = [
    LOG_VERBS.EDITED,
    LOG_VERBS.QUOTE_DETAILS_PROVIDED,
    LOG_VERBS.QUOTE_FEEDBACK_PROVIDED,
    LOG_VERBS.FEEDBACK_PROVIDED,
    LOG_VERBS.USER_INTEREST_REVALIDATED,
  ];
  const hasSubtitle = !logsWithoutSubtitle.includes(log.verb);
  const isTicketUpdate = ticketUpdatesLogs.includes(log.verb);

  const date = moment(log.created_at).format('dddd DD MMM YYYY');
  const hour = moment(log.created_at).format('HH:mm');
  const isToday = date === moment().format('dddd DD MMM YYYY');
  const renderedDate = isToday ? `Today ${hour}` : `${date} ${hour}`;

  const subtitle = getLogSubtitle(log);
  const titleData = { log, hasSubtitle, subtitle, config };
  const { email, phone_number, contact_name, type } = log.created_by || {};
  const ticketID =
    log.action_object?.data?.ticket_id || log.action_object?.data?.id || log.target?.data?.id;
  const { reasons = [] } = log.action_object?.data || {};
  const reasonType = log.action_object?.type;
  const hasReasons = reasons.length > 0;
  const reasonObservations = log.action_object?.data?.text;
  const isCarrierBid = log.verb === LOG_VERBS.TICKET_BID_RECEIVED;
  const isCarrierRejection = log.verb === LOG_VERBS.AUCTION_REJECTED;
  const isAuctionStarted = log.verb === LOG_VERBS.AUCTION_STARTED;
  const isInternalComment = log.verb === LOG_VERBS.COMMENTED;
  const isIntegrationUpdate = log.verb === LOG_VERBS.INTEGRATION_STATUS_UPDATE;
  const isTicketCompleted = log.verb === LOG_VERBS.DONE;

  return (
    <div className={className}>
      {/* title */}
      <LogEntryTitleContainer>
        <TitleLog titleData={titleData} />
        {hasID && (
          <TicketIdButton onClick={() => onIDClick(ticketID)}>
            {`ID.${ticketService.utils.getFormatedId(ticketID || '')}`}
          </TicketIdButton>
        )}
      </LogEntryTitleContainer>
      {/* body */}
      {hasReasons && (
        <LogReasonsContainer>
          {reasons.map((reason) => (
            <>
              <FlexParent>
                <BulletPoint />
                {reasonText[reasonType][reason.category]}
              </FlexParent>
            </>
          ))}
          {reasonObservations && <p>{reasonObservations}</p>}
        </LogReasonsContainer>
      )}
      {isCarrierBid && (
        <LogReasonsContainer>
          <LogReasonsFlexComponent>
            <FlexParent>
              <BulletPoint />
              <div>
                <b>{log.action_object.data.bidder.name}</b> -{' '}
                <IntlProvider>
                  <FormatPrice
                    value={log.action_object.data.cost.amount}
                    currency={log.action_object.data.cost.currency}
                  />
                </IntlProvider>
              </div>
            </FlexParent>
            {log.action_object?.data?.conditions && (
              <FlexParent>
                <LogConditionsLabel isInfo color="var(--grey700)">
                  Condiciones:{' '}
                </LogConditionsLabel>
                <LogConditions isInfo color="var(--grey700)">
                  {log.action_object.data.conditions}
                </LogConditions>
              </FlexParent>
            )}
          </LogReasonsFlexComponent>
        </LogReasonsContainer>
      )}
      {isCarrierRejection && (
        <LogReasonsContainer>
          <FlexParent>
            <BulletPoint />
            <div>
              <b>{log.created_by.name}</b>
            </div>
          </FlexParent>
        </LogReasonsContainer>
      )}
      {isAuctionStarted && (
        <LogReasonsContainer>
          {log.action_object.data.carrier_users.map((carrier) => (
            <>
              <FlexParent>
                <BulletPoint />
                {carrier.email}
              </FlexParent>
            </>
          ))}
          {reasonObservations && <p>{reasonObservations}</p>}
        </LogReasonsContainer>
      )}
      {isTicketUpdate && <LogItemTicketUpdateBody log={log} />}
      {isInternalComment && (
        <LogReasonsContainer>
          <FlexParent>
            <div>{log.message}</div>
          </FlexParent>
        </LogReasonsContainer>
      )}
      {isIntegrationUpdate && (
        <LogReasonsContainer>
          <FlexParent>
            <div>
              {log.action_object?.data?.external_order_code &&
                `${log.action_object.data.external_order_code} - `}
              {log.action_object?.data?.status_description}
            </div>
          </FlexParent>
        </LogReasonsContainer>
      )}
      {isTicketCompleted && (
        <LogReasonsContainer>
          <FlexParent>
            <div>
              If it's not integrated in next 5 minutes, please contact{' '}
              <b>xet-it-fo-product@xpo.com</b>
            </div>
          </FlexParent>
        </LogReasonsContainer>
      )}
      <LogItemNotificationFailedBody log={log} />
      <LogItemInfoContainer>
        <Badge variant={badgeVariants[type]}>{type}</Badge>
        <TextLog isInfo color="#94A6B8">
          {email || phone_number || contact_name} {`- ${log.created_at ? renderedDate : ''}`}
        </TextLog>
      </LogItemInfoContainer>
      <Separator {...separatorProps} />
    </div>
  );
};

export default styled(LogItem)`
  display: flex;
  padding: 16px 16px 0 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background: var(--primary-background);
  width: 100%;
  ${({ isAlert, unread }) =>
    isAlert &&
    unread &&
    css`
      border-left: 4px solid var(--error-900);
      margin-top: 1px;
    `}
  ${({ isAlert, unread }) =>
    isAlert &&
    !unread &&
    css`
      background: var(--grey100);
    `}
`;
